import React, { useCallback, useState } from 'react'
import { BlockWrapper, defineBlock, smartProps } from '@smartfire/js-block-engine'
import type { Phase, Poule, Equipe } from '../types'
import { Selector } from '../../components/Selector'
import { useCompetitionProps } from '../../hooks/useCompetitionProps'
import styles from './styles.module.scss'

import type { Params } from '../../hooks/useCompetitionProps/useParamsHandler'

interface Attributes {
    selected_equipe: Equipe | null
    selected_poule: Poule
    equipe_options: Equipe[]
    poules: Poule[]
    phases: Phase[]
}

type SelectorOption = { value: string; label: string }

const ALL_EQUIPES_OPTION: SelectorOption = {
    value: 'ALL_EQUIPES',
    label: 'Tous les clubs',
}

// todo(bug): poule not selected when going to previous page
const PouleSelector = ({
    showEquipeSelector = true,
    ...attributes
}: Attributes &
    Params & {
        showEquipeSelector?: boolean
    }) => {
    const [loadingSelector, setLoadingSelector] = useState<'phase' | 'poule' | 'equipe' | 'none'>(
        'none',
    )

    const onUpdated = useCallback(() => {
        setLoadingSelector('none')
    }, [])

    const {
        attributes: { selected_poule, equipe_options, poules, phases },
        params: { ext_equipe_id },
        setParams,
    } = useCompetitionProps<Attributes>({
        initDataFromPhpFirstRendering: attributes,
        allowedParamsUpdatesProps: {
            ext_equipe_id: true,
            ext_poule_id: true,
        },
        onFinish: onUpdated,
    })

    const equipeOptions: SelectorOption[] = [
        ALL_EQUIPES_OPTION,
        ...equipe_options.map((x) => ({ value: x.ext_equipeId, label: x.libelle })),
    ]

    const [selectedPhase, setSelectedPhase] = useState(() =>
        phases.find((p) => p.id === selected_poule.phaseId),
    )

    const pouleOptions: SelectorOption[] = poules
        .filter((p) => p.phaseId === selectedPhase?.id)
        .map((x) => ({
            value: x.ext_pouleId,
            label: x.libelle,
        }))

    const phaseOptions: SelectorOption[] = phases.map((x) => ({
        value: x.ext_phaseId,
        label: x.libelle,
    }))

    const showPhaseSelector = true // 03/08/2023 always show phase selector

    const nbSelectors = 1 + (showPhaseSelector ? 1 : 0) + (showEquipeSelector ? 1 : 0)

    if (!selected_poule) {
        return null
    }

    return (
        <div
            className={styles.wrapper}
            style={{
                gridTemplateColumns: `repeat(${nbSelectors}, 1fr)`,
            }}
        >
            {showPhaseSelector && (
                <Selector
                    loading={loadingSelector === 'phase'}
                    label="Sélectionner une phase"
                    handleSelect={(x) => {
                        if (x === selectedPhase?.ext_phaseId) return
                        const newPhase = phases.find((p) => p.ext_phaseId === x)
                        setSelectedPhase(newPhase)
                        const newPouleOptions = poules.filter((p) => p.phaseId === newPhase?.id)
                        if (newPouleOptions.length) {
                            setLoadingSelector('phase')
                            setParams(
                                'ext_poule_id',
                                newPouleOptions[newPouleOptions.length - 1].ext_pouleId,
                            )
                        }
                    }}
                    value={selectedPhase?.ext_phaseId ?? ''}
                    options={phaseOptions}
                />
            )}
            <Selector
                loading={loadingSelector === 'poule'}
                label="Sélectionner une poule"
                handleSelect={(x) => {
                    if (x === selected_poule.ext_pouleId) return
                    setLoadingSelector('poule')
                    setParams('ext_poule_id', x)
                }}
                value={selected_poule.ext_pouleId}
                options={pouleOptions}
                fallbackLabel="-"
            />
            {showEquipeSelector && (
                <Selector
                    loading={loadingSelector === 'equipe'}
                    label="Filtrer par club"
                    handleSelect={(v) => {
                        setLoadingSelector('equipe')
                        v === ALL_EQUIPES_OPTION.value
                            ? setParams('ext_poule_id', selected_poule.ext_pouleId)
                            : setParams('ext_equipe_id', v)
                    }}
                    value={ext_equipe_id || ALL_EQUIPES_OPTION.value}
                    options={equipeOptions}
                />
            )}
        </div>
    )
}

const PouleSelectorBlock = (attributes) => (
    <BlockWrapper.Without>
        <PouleSelector {...attributes} />
    </BlockWrapper.Without>
)

export default defineBlock({
    Block: PouleSelectorBlock,
    smartProps: {
        saisonFromQuery: smartProps.string.withDefault('').contentServerSide(),
    },
})
